import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { loginRedirect } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Users, ArrowLeft, Trash2, Plus } from "lucide-react";

export default function ClubMemberships() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [known, setKnown] = useState([]);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({
    club_id: "", custom_name: "", role: "Member", chapter: "", member_id: "",
    joined_year: "", is_public: true,
  });
  const [busy, setBusy] = useState(false);

  const reload = () => {
    if (!user) return;
    api.get("/citizen-card/clubs").then(({ data }) => setMine(data)).catch(() => {});
  };
  useEffect(() => { api.get("/citizen-card/known-clubs").then(({ data }) => setKnown(data)).catch(() => {}); }, []);
  useEffect(reload, [user]); // eslint-disable-line

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <h2 className="heading text-3xl font-extrabold mb-3">Please login</h2>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="clubs-login">Login with Google</Button>
      </div>
    );
  }

  const submit = async () => {
    if (!form.club_id) return toast.error("Choose a club");
    if (form.club_id === "custom" && !form.custom_name.trim()) return toast.error("Custom club name required");
    setBusy(true);
    try {
      await api.post("/citizen-card/clubs", {
        ...form,
        joined_year: form.joined_year ? parseInt(form.joined_year) : null,
      });
      toast.success("Membership added");
      setForm({ club_id: "", custom_name: "", role: "Member", chapter: "", member_id: "", joined_year: "", is_public: true });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this membership?")) return;
    try { await api.delete(`/citizen-card/clubs/${id}`); reload(); toast.success("Removed"); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6" data-testid="clubs-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="clubs-back"><ArrowLeft size={16} className="mr-1" /> Back</Button>
        <h1 className="heading text-2xl font-extrabold flex items-center gap-2"><Users className="text-[#E05E36]" /> Club & Society Memberships</h1>
      </div>

      <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Plus size={16} /> Add a membership</CardTitle></CardHeader>
        <CardContent className="space-y-3" data-testid="club-add-form">
          <Select value={form.club_id} onValueChange={(v) => setForm({ ...form, club_id: v })}>
            <SelectTrigger data-testid="club-select"><SelectValue placeholder="Choose club" /></SelectTrigger>
            <SelectContent>
              {known.map((c) => <SelectItem key={c.club_id} value={c.club_id}>{c.icon} {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {form.club_id === "custom" && (
            <Input value={form.custom_name} onChange={(e) => setForm({ ...form, custom_name: e.target.value })} placeholder="Custom club/society name" data-testid="club-custom-name" />
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role (Member / President…)" data-testid="club-role" />
            <Input value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })} placeholder="Chapter" data-testid="club-chapter" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} placeholder="Member ID" data-testid="club-member-id" />
            <Input type="number" value={form.joined_year} onChange={(e) => setForm({ ...form, joined_year: e.target.value })} placeholder="Joined year" data-testid="club-year" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Show on my public card</span>
            <Switch checked={form.is_public} onCheckedChange={(v) => setForm({ ...form, is_public: v })} data-testid="club-public" />
          </div>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="club-submit">
            {busy ? "Adding…" : "Add membership"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">My memberships ({mine.length})</CardTitle></CardHeader>
        <CardContent>
          {mine.length === 0 ? (
            <div className="text-sm text-stone-500 text-center py-6" data-testid="clubs-empty">No memberships yet.</div>
          ) : (
            <div className="space-y-2">
              {mine.map((m) => (
                <div key={m.membership_id} className="flex items-center justify-between border-b border-stone-100 py-2" data-testid={`club-row-${m.membership_id}`}>
                  <div>
                    <div className="font-semibold">{m.icon} {m.club_name}</div>
                    <div className="text-xs text-stone-500">
                      {m.role || "Member"}{m.chapter ? ` · ${m.chapter}` : ""}{m.joined_year ? ` · Since ${m.joined_year}` : ""}
                      {!m.is_public && <span className="ml-2 text-amber-600">(private)</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove(m.membership_id)} className="rounded-full" data-testid={`club-remove-${m.membership_id}`}>
                    <Trash2 size={14} className="text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
